import { DateTime } from '@ksyos/date-time';
import type { ValidationErrors } from '../../../../../utils/validation';
import type { IKlachten } from './KlachtenAddEditModal';
import { CheckMarkButton } from '../../../../elements/buttons/checkMarkButton/CheckMarkButton';
import { FormInlineGroup, FormParagraph, NullableInputText } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { KlachtenAddEditModal } from './KlachtenAddEditModal';

interface IKlachtenCustomControlData {
    klacht: string | null;
    klachten: IKlachten | null;
}

interface IKlachtenAddEditProps {
    customControlData: IKlachtenCustomControlData;
    errors: ValidationErrors;
    handleChange: (customControl: Partial<IKlachtenCustomControlData>) => Promise<void>;
    isRequired: boolean;
    validateOne: (name: keyof IKlachtenCustomControlData) => Promise<void>;
}

interface IKlachtenAddEditState {
    showModal?: boolean;
}

export class KlachtenAddEdit extends Component<IKlachtenAddEditProps, IKlachtenAddEditState> {
    public render() {
        return (
            <>
                <HorizontalFormGroup
                    hasErrors={
                        this.props.errors.has('customControlData.klacht') ||
                        this.props.errors.has('customControlData.klachten')
                    }
                >
                    <FormLabel isRequired={this.props.isRequired}>Klachten</FormLabel>
                    <InputContainer>
                        <NullableInputText
                            error={this.props.errors.get('customControlData.klacht')}
                            onChange={async (value) => await this.props.handleChange({ klacht: value })}
                            onDoValidate={async () => await this.props.validateOne('klacht')}
                            placeholder="Omschrijf de klachten of klik en kies uit de lijst..."
                            value={this.props.customControlData.klacht}
                        />
                    </InputContainer>
                    <CheckMarkButton
                        automation="cardioButtonComplaints"
                        hasError={
                            this.props.errors.has('customControlData.klacht') ||
                            this.props.errors.has('customControlData.klachten')
                        }
                        isChecked={!!this.props.customControlData.klachten}
                        onClick={async () => await this.setState({ showModal: true })}
                    >
                        Klachten
                    </CheckMarkButton>
                </HorizontalFormGroup>

                {this.state.showModal && (
                    <KlachtenAddEditModal
                        anamneseData={this.props.customControlData.klachten}
                        deleteAnamneseData={async () => {
                            await this.props.handleChange({ klachten: null });
                            await this.props.validateOne('klacht');
                            await this.setState({ showModal: false });
                        }}
                        onClose={async () => await this.setState({ showModal: false })}
                        setAnamneseData={async (klacht) => {
                            await this.props.handleChange({ klachten: klacht });
                            await this.props.validateOne('klacht');
                            await this.setState({ showModal: false });
                        }}
                    />
                )}
            </>
        );
    }
}

export function KlachtenRead(props: { customControlData: IKlachtenCustomControlData }) {
    return (
        <HorizontalFormGroupRead>
            <FormLabel>Klachten</FormLabel>
            <InputContainer>
                <FormParagraph>
                    {props.customControlData.klacht && <FormInlineGroup value={props.customControlData.klacht} />}

                    {props.customControlData.klachten && (
                        <>
                            {props.customControlData.klachten.klachten_sinds && (
                                <FormInlineGroup
                                    label={'Klacht sinds'}
                                    value={DateTime.from(props.customControlData.klachten.klachten_sinds).format(
                                        'DD-MM-YYYY'
                                    )}
                                />
                            )}
                            {props.customControlData.klachten.klachten_tijdens_het_maken_van_het_ECG !== null && (
                                <FormInlineGroup
                                    label={'Klachten tijdens het maken van het ECG?'}
                                    value={props.customControlData.klachten.klachten_tijdens_het_maken_van_het_ECG}
                                />
                            )}
                            {props.customControlData.klachten.pijn_op_de_borst !== null && (
                                <FormInlineGroup
                                    label={'Pijn op de borst'}
                                    value={props.customControlData.klachten.pijn_op_de_borst}
                                />
                            )}
                            {props.customControlData.klachten.dyspnoe !== null && (
                                <FormInlineGroup
                                    label={'Dyspnoe'}
                                    value={props.customControlData.klachten.dyspnoe}
                                />
                            )}
                            {props.customControlData.klachten.palpitaties !== null && (
                                <FormInlineGroup
                                    label={'Palpitaties'}
                                    value={props.customControlData.klachten.palpitaties}
                                />
                            )}
                            {props.customControlData.klachten.moeheid !== null && (
                                <FormInlineGroup
                                    label={'Moeheid'}
                                    value={props.customControlData.klachten.moeheid}
                                />
                            )}
                            {props.customControlData.klachten.conditievermindering !== null && (
                                <FormInlineGroup
                                    label={'Conditievermindering'}
                                    value={props.customControlData.klachten.conditievermindering}
                                />
                            )}
                            {props.customControlData.klachten.hartgeruis !== null && (
                                <FormInlineGroup
                                    label={'Hartgeruis'}
                                    value={props.customControlData.klachten.hartgeruis}
                                />
                            )}
                            {props.customControlData.klachten.collaps !== null && (
                                <FormInlineGroup
                                    label={'Collaps'}
                                    value={props.customControlData.klachten.collaps}
                                />
                            )}
                            {props.customControlData.klachten.klachtenvrij !== null && (
                                <FormInlineGroup
                                    label={'Klachtenvrij'}
                                    value={props.customControlData.klachten.klachtenvrij}
                                />
                            )}
                            {props.customControlData.klachten.screening !== null && (
                                <FormInlineGroup
                                    label={'Screening'}
                                    value={props.customControlData.klachten.screening}
                                />
                            )}
                            {props.customControlData.klachten.overig && (
                                <FormInlineGroup
                                    label={'Overig'}
                                    value={props.customControlData.klachten.overig}
                                />
                            )}
                        </>
                    )}
                </FormParagraph>
            </InputContainer>
        </HorizontalFormGroupRead>
    );
}
